/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Collapse } from 'antd'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { PluginBrand } from '@sportninja/common/features/account/apiAccess'
import { paragraphXSmall, weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { DisplayGroup } from './DisplayGroup'
import { EmbedGroup } from './EmbedGroup'
import { TabsGroup } from './TabsGroup'
import { ThemeGroup } from './ThemeGroup'

interface PluginSettingsPanelProps {
  /** The tenant's brand: what the plugin paints for unset colours. */
  brand: PluginBrand
}

type GroupKey = 'embed' | 'tabs' | 'theme' | 'display'

/** The groups open on arrival; the rest start closed. */
const OPEN_BY_DEFAULT: GroupKey[] = ['embed', 'theme']

/**
 * The snippet builder's settings column: one collapsible group per part of
 * the embed, reading and writing the builder form in `FormikProvider`.
 */
export const PluginSettingsPanel = ({ brand }: PluginSettingsPanelProps) => {
  const groups: {
    key: GroupKey
    title: string
    description: string
    body: ReactNode
  }[] = [
    {
      key: 'embed',
      title: 'Embed',
      description:
        "Your plugin's entry point: the page it opens on, the first tab and URL sync",
      body: <EmbedGroup />,
    },
    {
      key: 'tabs',
      title: 'Tabs',
      description:
        'Show or hide tabs on organization, competition and team pages',
      body: <TabsGroup />,
    },
    {
      key: 'theme',
      title: 'Theme',
      description:
        "Colours, fonts, corners, spacing and layout (the snippet's theme option)",
      body: <ThemeGroup brand={brand} />,
    },
    {
      key: 'display',
      title: 'Display',
      description: 'Header, schedule, footer and the communication feed',
      body: <DisplayGroup />,
    },
  ]

  return (
    <aside aria-label={capitalizeWords('Plugin settings')} css={panelStyle}>
      <Collapse
        css={collapseStyle}
        defaultActiveKey={OPEN_BY_DEFAULT}
        expandIconPosition='end'
        items={groups.map(({ key, title, description, body }, index) => ({
          key,
          label: (
            <div css={groupHeaderStyle}>
              {/* Where the group sits in the column, so a long scroll keeps its place. */}
              <span css={stepStyle} data-step aria-hidden='true'>
                {index + 1}
              </span>
              <div css={groupHeadingStyle}>
                <Text
                  variant='paragraphMedium'
                  weight='semiBold'
                  color={colors.WHITE}
                >
                  {capitalizeWords(title)}
                </Text>
                <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                  {description}
                </Text>
              </div>
            </div>
          ),
          children: body,
        }))}
      />
    </aside>
  )
}

// The dev-server type check doesn't see SECONDARY_150 on `colors`, though it is defined.
const palette = colors as Record<string, string>

const panelStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const groupHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`

const groupHeadingStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const stepStyle = css`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background-color: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  ${paragraphXSmall}
  ${weightSemiBold}
`

/**
 * antd Collapse on the dark account cards: each group its own 8px card. An open
 * group's header is raised (lighter fill, divider, PRIMARY step) and sticks to
 * the top of the scrolling settings column, so a long group keeps its name in
 * view. No `overflow: hidden` on the item: it would break the sticky header.
 */
const collapseStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: transparent;
  border: none;

  && > .ant-collapse-item {
    border: 1px solid ${colors.SOFT_STEEL};
    border-radius: 8px;
    background-color: ${colors.HEADER};
  }
  && > .ant-collapse-item:last-child {
    border-radius: 8px;
  }
  && > .ant-collapse-item-active {
    border-color: ${colors.SECONDARY_100};
  }
  && > .ant-collapse-item > .ant-collapse-header {
    align-items: center !important;
    padding: 12px 16px !important;
    border-radius: 8px;
  }
  && > .ant-collapse-item-active > .ant-collapse-header {
    position: sticky;
    top: 0;
    z-index: 1;
    border-radius: 8px 8px 0 0;
    background-color: ${palette.SECONDARY_150};
    border-bottom: 1px solid ${colors.SECONDARY_100};
  }
  && > .ant-collapse-item-active > .ant-collapse-header [data-step] {
    background-color: ${colors.PRIMARY};
    color: ${PRIMARY_BUTTON_CONTENT.text};
  }
  .ant-collapse-expand-icon {
    color: ${colors.NEUTRAL_50};
  }
  && > .ant-collapse-item-active .ant-collapse-expand-icon {
    color: ${colors.WHITE};
  }
  && .ant-collapse-content {
    background: transparent;
    border-top: none;
    border-radius: 0 0 8px 8px;
  }
  && .ant-collapse-content > .ant-collapse-content-box {
    padding: 16px;
  }
`
